/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { SelectFieldOption } from '@squiz/sds';
import cloneDeep from 'lodash/cloneDeep';
import isString from 'lodash/isString';
import parseInt from 'lodash/parseInt';
import property from 'lodash/property';
import { ChangeEvent, MouseEvent, useCallback, useState } from 'react';
import { RULESET_ROW_INITIAL } from '../../Screens/SegmentBuilder/SegmentBuilder.constants';
import { UISegmentData } from '../../Screens/SegmentBuilder/defs/UISegmentData.defs';

/**
 * Custom hook to retrieve the necessary data for the segment builder form.
 * @param props Props passed to `useSegmentBuilderForm`.
 * @returns Helper object containing data for the builder form.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useSegmentBuilderForm = (props: UISegmentData) => {
  const { segmentName, segmentId, segmentDescription, segmentRules } = props;
  const currentName = segmentName;
  const currentId = segmentId;
  const currentDescription = segmentDescription;
  const [conditions, setConditions] = useState(cloneDeep(segmentRules));
  const [rulesetOperator, setRulesetOperator] = useState(conditions.operator ? conditions.operator : 'or');

  const updateRuleConditions = useCallback(
    (target: HTMLInputElement, value: string | number): void => {
      let targetName = target.name as string;
      const currentTarget = target.closest('.segment-rule') as HTMLElement;
      const targetRuleset = currentTarget.getAttribute('data-ruleset') as string;
      const targetRuleIndex = currentTarget.getAttribute('data-rule-index') as string;
      const newConditions = cloneDeep(conditions);

      if (targetName === `count` && isString(value)) {
        value = parseInt(value as string);
        targetName = `value`;
      }

      if (targetName === 'property') {
        const typeValue = (value as string).startsWith(`$event`) ? `number` : `string`;
        // @ts-ignore.
        newConditions.rulesets[parseInt(targetRuleset)].rules[parseInt(targetRuleIndex)].type = typeValue;
      }

      // @ts-ignore.
      newConditions.rulesets[parseInt(targetRuleset)].rules[parseInt(targetRuleIndex)][targetName] = value;
      setConditions(newConditions);
    },
    [conditions, setConditions],
  );

  const handleRuleSelectsChange = useCallback(
    (ref: React.RefObject<HTMLInputElement>, selected: string): void => {
      const currentTarget = ref.current;
      if (currentTarget !== null) {
        // Update the state with the new value
        updateRuleConditions(currentTarget, selected);
      }
    },
    [updateRuleConditions],
  );

  const handleRuleInputsChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const target = event.target as HTMLInputElement;
      updateRuleConditions(target, event.target.value);
    },
    [updateRuleConditions],
  );

  const handleRuleSetOperator = useCallback(
    (option: SelectFieldOption): void => {
      setRulesetOperator(option.value);
      const newConditions = { ...conditions };
      newConditions.operator = option.value;
      setConditions(newConditions);
    },
    [conditions],
  );

  const handleRuleOperatorFirst = useCallback(
    (option: SelectFieldOption): void => {
      const newConditions = { ...conditions };
      newConditions.rulesets[0].operator = option.value;
      setConditions(newConditions);
    },
    [conditions],
  );

  const handleRuleOperatorLast = useCallback(
    (option: SelectFieldOption): void => {
      const newConditions = { ...conditions };
      newConditions.rulesets[1].operator = option.value;
      setConditions(newConditions);
    },
    [conditions],
  );

  const handleAddRule = useCallback(
    (event: MouseEvent<HTMLButtonElement>): void => {
      const currentTarget = event.target as HTMLElement;
      const targetValue = currentTarget.closest('button') as HTMLButtonElement;
      const currentRuleset = targetValue.getAttribute('data-ruleset') as string;
      const currentRules = conditions.rulesets[parseInt(currentRuleset)].rules;

      // Add a new rule with a unique id
      currentRules.push({
        comparison: ``,
        id: window.crypto.randomUUID(),
        property: '',
        type: '',
        value: '',
      });
      const newConditions = cloneDeep(conditions);
      newConditions.rulesets[parseInt(currentRuleset)].rules = currentRules;
      setConditions(newConditions);
    },
    [conditions],
  );

  const handleAddInitialRuleset = useCallback((): void => {
    conditions.rulesets.push(RULESET_ROW_INITIAL);
    const newConditions = cloneDeep(conditions);
    newConditions.rulesets = conditions.rulesets;
    setConditions(newConditions);
  }, [conditions]);

  const handleAddRuleset = useCallback(
    (type = `default`): void => {
      // Add one to the preceding ID
      const newId = Number(property(`rulesets[0].id`)(conditions) || 0) + 1;
      conditions.rulesets.push({
        id: newId.toString(),
        operator: 'and',
        rules: [
          {
            comparison: ``,
            id: window.crypto.randomUUID(),
            property: '',
            type: '',
            value: '',
          },
        ],
        type,
      });
      const newConditions = cloneDeep(conditions);
      newConditions.rulesets = conditions.rulesets;
      setConditions(newConditions);
    },
    [conditions],
  );

  const handleDeleteRuleset = useCallback(
    (event: MouseEvent<HTMLElement>): void => {
      const currentTarget = (event.target as HTMLElement).closest('button') as HTMLButtonElement;
      const targetRuleset = currentTarget.getAttribute('data-ruleset') as string;
      conditions.rulesets.splice(Number(targetRuleset), 1);
      const newConditions = cloneDeep(conditions);
      newConditions.rulesets = conditions.rulesets;
      setConditions(newConditions);
    },
    [conditions],
  );

  const handleDeleteRule = useCallback(
    (event: MouseEvent<HTMLElement>): void => {
      const currentTarget = (event.target as HTMLElement).closest('button') as HTMLButtonElement;
      const targetRuleset = currentTarget.getAttribute('data-ruleset') as string;
      const targetRule = currentTarget.getAttribute('data-rule') as string;
      const currentRules = conditions.rulesets[parseInt(targetRuleset)].rules;
      const newRules = currentRules.filter((item: { id: string }) => item.id !== targetRule);

      const newConditions = cloneDeep(conditions);
      newConditions.rulesets[parseInt(targetRuleset)].rules = newRules;
      setConditions(newConditions);
    },
    [conditions],
  );

  return {
    conditions,
    currentDescription,
    currentId,
    currentName,
    handleAddInitialRuleset,
    handleAddRule,
    handleAddRuleset,
    handleDeleteRule,
    handleDeleteRuleset,
    handleRuleInputsChange,
    handleRuleOperatorFirst,
    handleRuleOperatorLast,
    handleRuleSelectsChange,
    handleRuleSetOperator,
    rulesetOperator,
    updateRuleConditions,
  };
};
